import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { ConfigurationRelease, PCInstance, StartedEntry } from '../types/database';
import {
  dequeueForLaunch,
  displayName as queueDisplayName,
  fullName,
  isNotQueuedError,
  partyMarker,
} from '../services/queue';
import {
  crashRecoveryView,
  currentOperatorId,
  queueMove,
  queueRequeue,
  queueRestart,
  type TileRecoveryDeps,
} from '../services/tileRecovery';
import {
  FAKE_NEARLY_DONE_LEAD_MS,
  fakeAgentArmStuck,
  fakeAgentCrash,
  fakeAgentLaunch,
  fakeAgentMarkNearlyDone,
  fakeAgentStop,
  fakeCrashRecovery,
  getFakeAgent,
  subscribeFakeAgent,
} from '../services/fakeAgent';
import { useQueueDequeueOptional } from '../contexts/QueueDequeueContext';
import { useQueueDispatchOptional } from '../contexts/QueueDispatchContext';
import { useSettings } from '../contexts/SettingsContext';
import { DEV_TOOLS_ENABLED, readFakeLoadingSeconds, readFakeRunSeconds } from '../utils/env';
import { broadcastNextUp } from './NextUpWidget';
import { nameToColor, playDangerAlert } from './PCTile';
import LanguageFlag, { hasLanguageFlag } from './LanguageFlag';
import type { ToastData } from './Toast';

// STAGING-ONLY stand-in for PCTile: a room PC without an ip_address (the
// staging isolation script wipes venue addresses) gets this tile when
// Settings -> Staging tools -> "Fake PC tiles" is on. Its "agent" is the
// in-memory store in services/fakeAgent.ts: idle -> loading -> running ->
// done -> idle, with the same queue wiring a real tile has:
//  - reports {online, running, canLaunch} to QueueDispatchContext and
//    registers a launch function, so the "Volgende bezoeker" pop-up lists it
//    as a free PC and launches THROUGH it (pinned dequeue with this pc id),
//  - shows the visitor playing here (fn_get_started_today via startedByPc),
//  - offers Opnieuw starten / Verplaats naar andere PC / Terug in wachtrij
//    through the shared services/tileRecovery helper,
//  - fires the nearly-done broadcast (banner + display overlay + chime) 15 s
//    before the fake run ends, like PCTile does on last_level_warning.
// Never calls pcAgent. Gated on DEV_TOOLS_ENABLED (also in Dashboard).

interface FakePCTileProps {
  pcInstance: PCInstance;
  /** The room's configured releases: every one counts as installed here. */
  experiences: ConfigurationRelease[];
  isAdmin?: boolean;
  onExperienceStateChange?: (pcId: string, running: boolean) => void;
  onToast?: (t: ToastData) => void;
}

function useFakeAgent(pcId: string) {
  return useSyncExternalStore(
    (cb) => subscribeFakeAgent(pcId, cb),
    () => getFakeAgent(pcId),
    () => getFakeAgent(pcId),
  );
}

function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

export default function FakePCTile({
  pcInstance,
  experiences,
  isAdmin = false,
  onExperienceStateChange,
  onToast,
}: FakePCTileProps) {
  const state = useFakeAgent(pcInstance.id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [languagePick, setLanguagePick] = useState<ConfigurationRelease | null>(null);

  const queueCtx = useQueueDequeueOptional();
  const queueCtxRef = useRef(queueCtx);
  useEffect(() => {
    queueCtxRef.current = queueCtx;
  }, [queueCtx]);
  // Same ref pattern as PCTile: the report/registry effects must not re-fire
  // on every pcReports change of the context value.
  const dispatchCtx = useQueueDispatchOptional();
  const dispatchCtxRef = useRef(dispatchCtx);
  useEffect(() => {
    dispatchCtxRef.current = dispatchCtx;
  }, [dispatchCtx]);
  const onExperienceStateChangeRef = useRef(onExperienceStateChange);
  useEffect(() => {
    onExperienceStateChangeRef.current = onExperienceStateChange;
  }, [onExperienceStateChange]);
  const { settings } = useSettings();
  const settingsRef = useRef(settings);
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  const pcName = pcInstance.pc_name || `PC ${pcInstance.player_index ?? '?'}`;
  const headerName = isAdmin
    ? `${pcInstance.player_index ?? '?'}: ${pcInstance.pc_name || 'Unknown PC'}`
    : (pcInstance.player_index?.toString() || '?');

  const run = state.run;
  const phase = state.phase;
  // Crash ladder, read exactly like PCTile reads the agent's crash_recovery
  // block: a relaunch in flight still counts as running (the visitor never
  // left), a gave-up crash frees the PC but keeps it out of dispatch while
  // its visitor is unresolved.
  const crashRecovery = fakeCrashRecovery(state);
  const crashView = crashRecoveryView(crashRecovery);
  const dispatchRunning = phase === 'loading' || phase === 'running' || phase === 'recovering';

  // --- Launch (same contract as PCTile.handleLaunchExperience) --------------
  // Flip the fake agent first (the tile shows "Laden..." immediately), then
  // pop the queue: pinned entry from the dispatch pop-up, else the widget's
  // dequeue-on-launch selection. Resolves true: the fake launch cannot fail.
  const handleLaunchExperience = async (
    experienceId: string,
    version: string,
    language: string | null,
    opts?: { dequeueEntryId?: string },
  ): Promise<boolean> => {
    const release =
      experiences.find(
        (e) => e.experience_id === experienceId && (e.experience_release?.version || '1.0.0') === version,
      ) ?? experiences.find((e) => e.experience_id === experienceId);
    setError(null);
    setLanguagePick(null);
    fakeAgentLaunch(pcInstance.id, {
      experienceId,
      name: release?.experiences?.name || 'Onbekende ervaring',
      version,
      language,
      loadingMs: readFakeLoadingSeconds() * 1000,
      runMs: readFakeRunSeconds() * 1000,
    });

    const operatorId = currentOperatorId();
    const qc = queueCtxRef.current;
    const dequeueTargetId =
      opts?.dequeueEntryId ?? (qc?.dequeueOnLaunch ? qc.selectedEntry?.queue_entry_id : undefined);
    if (dequeueTargetId && operatorId) {
      try {
        await dequeueForLaunch(dequeueTargetId, operatorId, pcInstance.id);
      } catch (err) {
        if (!isNotQueuedError(err)) {
          console.error('fake tile: queue dequeue after launch failed', err);
          setError('Launch gelukt, maar wachtrij niet bijgewerkt. Controleer handmatig.');
        }
      }
      qc?.setSelected(null);
    }
    return true;
  };
  const launchRef = useRef(handleLaunchExperience);
  launchRef.current = handleLaunchExperience;

  // Idle button click: 0/1 language launches directly, 2+ opens the picker.
  // Multiplayer releases launch like any other here (no MP session modal:
  // that flow needs real agents).
  const handleExperienceClick = (release: ConfigurationRelease) => {
    const langs = release.experiences?.supported_languages ?? [];
    const version = release.experience_release?.version || '1.0.0';
    if (langs.length === 0) {
      void handleLaunchExperience(release.experience_id, version, null);
    } else if (langs.length === 1) {
      void handleLaunchExperience(release.experience_id, version, langs[0].language?.short_name || null);
    } else {
      setLanguagePick(release);
    }
  };

  // --- Visitor from the queue (same guard as PCTile) --------------------------
  // A crashed run counts as "still playing here" until the operator resolves
  // it, exactly like on a real tile. Declared before the dispatch report,
  // which reads it.
  const queueVisitor: StartedEntry | null = useMemo(() => {
    if ((!dispatchRunning && phase !== 'gaveup') || !run) return null;
    const s = queueCtx?.startedByPc.get(pcInstance.id) ?? null;
    if (!s) return null;
    const eventAt = Date.parse(s.last_event_at || s.started_at);
    if (Number.isFinite(eventAt) && eventAt < run.launchedAt - 5 * 60_000) return null;
    return s;
  }, [dispatchRunning, phase, run, queueCtx, pcInstance.id]);

  // Same truth table as PCTile: relaunch in flight OR a gave-up crash whose
  // visitor is still on this PC.
  const dispatchRecovering = phase === 'recovering' || (phase === 'gaveup' && !!queueVisitor);

  // --- Dispatch wiring (mirrors PCTile) --------------------------------------
  useEffect(() => {
    dispatchCtxRef.current?.reportPC(pcInstance.id, {
      pcName: pcInstance.pc_name ?? pcInstance.id,
      online: true,
      running: dispatchRunning,
      canLaunch: true,
      recovering: dispatchRecovering,
    });
  }, [pcInstance.id, pcInstance.pc_name, dispatchRunning, dispatchRecovering]);
  useEffect(() => {
    onExperienceStateChangeRef.current?.(pcInstance.id, dispatchRunning);
  }, [pcInstance.id, dispatchRunning]);
  useEffect(() => {
    // Mount-scoped on purpose (see PCTile): the provider wraps the tiles.
    const pcId = pcInstance.id;
    dispatchCtxRef.current?.registerLaunch(pcId, (release, language, dequeueEntryId) =>
      launchRef.current(
        release.experience_id,
        release.experience_release?.version || '1.0.0',
        language,
        { dequeueEntryId },
      ),
    );
    return () => {
      dispatchCtxRef.current?.retractPC(pcId);
      dispatchCtxRef.current?.unregisterLaunch(pcId);
      onExperienceStateChangeRef.current?.(pcId, false);
    };
  }, [pcInstance.id]);

  // --- Nearly done: what PCTile does on the agent's last_level_warning ------
  useEffect(() => {
    if (phase !== 'running' || !run || run.endsAt === null || run.nearlyDoneFired) return;
    if (Date.now() < run.endsAt - FAKE_NEARLY_DONE_LEAD_MS) return;
    fakeAgentMarkNearlyDone(pcInstance.id);
    const head = queueCtxRef.current?.headEntry ?? null;
    if (head) {
      // Fans out to the dashboard banner, other tabs and the Electron display.
      broadcastNextUp({ displayName: queueDisplayName(head), pcName });
    }
    const s = settingsRef.current;
    if (s.nearlyDoneSoundEnabled) playDangerAlert(s.nearlyDoneVolume);
  }, [phase, run, state.tick, pcInstance.id, pcName]);

  const recoveryDeps = (): TileRecoveryDeps => ({
    pcId: pcInstance.id,
    pcName,
    experiences,
    dispatchCtx,
    queueCtx,
    stop: async () => {
      fakeAgentStop(pcInstance.id);
      return true;
    },
    relaunch: (experienceId, version, language, dequeueEntryId) =>
      handleLaunchExperience(experienceId, version, language, { dequeueEntryId }),
    setBusy,
    setError,
    // Short pause so the idle -> loading flip is visible.
    relaunchDelayMs: 300,
  });
  const currentForRecovery = run ? { id: run.experienceId, version: run.version } : null;

  // STOP with the same confirm a real tile asks for.
  const handleStop = () => {
    if (!run) return;
    if (!window.confirm(`Are you sure you want to stop "${run.name}"?`)) return;
    setError(null);
    fakeAgentStop(pcInstance.id);
  };

  // Crash drill: first crash inside the window is auto-restarted with the same
  // visitor and the same remaining time, the next one trips the loop guard.
  const handleCrash = () => {
    const rung = fakeAgentCrash(pcInstance.id);
    if (!rung) return;
    onToast?.({
      kind: 'error',
      message:
        rung === 'gave_up'
          ? `${pcName}: auto-herstel gestopt (simulatie, 2e crash)`
          : `${pcName}: ervaring gecrasht (simulatie, exit 3), automatisch herstarten`,
    });
  };

  // --- Timers -------------------------------------------------------------------
  const now = Date.now();
  const elapsedS = run?.runStartedAt ? (now - run.runStartedAt) / 1000 : 0;
  const remainingS = run?.endsAt ? Math.max(0, (run.endsAt - now) / 1000) : 0;
  const runPct = run ? Math.min(100, (elapsedS / (run.runMs / 1000)) * 100) : 0;
  // Loading bar: climbs to 95% over the loading time and parks there (a stuck
  // start parks at 95% forever, like a real tile waiting for experience_loaded).
  const loadPct = run
    ? run.loadingMs > 0
      ? Math.min(95, ((now - run.launchedAt) / run.loadingMs) * 95)
      : 95
    : 0;
  const nearlyDone = phase === 'running' && !!run?.nearlyDoneFired;

  // `actions: false` keeps the visitor line but hides the buttons — the rung
  // where the agent is still relaunching and the operator decides nothing.
  const renderQueueVisitor = (opts?: { actions?: boolean }) => {
    if (!queueVisitor) return null;
    const marker = partyMarker(queueVisitor);
    return (
      <div className="tile-queue-visitor">
        <div className="tile-queue-visitor-line" title="Bezoeker uit de wachtrij die op deze PC speelt">
          <LanguageFlag code={queueVisitor.play_language} className="tile-queue-flag" />
          <span className="tile-queue-name">{fullName(queueVisitor)}</span>
          {marker ? <span className="tile-queue-party">{marker}</span> : null}
        </div>
        {opts?.actions !== false && (
        <div className="tile-queue-actions">
          <button
            className="tile-queue-btn"
            onClick={() => {
              if (currentForRecovery) void queueRestart(recoveryDeps(), queueVisitor, currentForRecovery);
            }}
            disabled={busy || !currentForRecovery}
            title="Stop en start dezelfde ervaring opnieuw voor deze bezoeker (zelfde taal, zelfde plek in de administratie)."
          >
            Opnieuw starten
          </button>
          <button
            className="tile-queue-btn"
            onClick={() => void queueMove(recoveryDeps(), queueVisitor, currentForRecovery)}
            disabled={busy || !dispatchCtx}
            title="Stop hier en kies een andere PC voor deze bezoeker."
          >
            Verplaats naar andere PC
          </button>
          <button
            className="tile-queue-btn"
            onClick={() => void queueRequeue(recoveryDeps(), queueVisitor)}
            disabled={busy}
            title="Stop en zet de bezoeker terug in de wachtrij op de oorspronkelijke plek."
          >
            Terug in wachtrij
          </button>
        </div>
        )}
      </div>
    );
  };

  // Same body as PCTile's crash ladder (styles in index.css).
  const renderCrashRecoveryBody = () => {
    if (!crashView) return null;
    const gaveUp = crashView.tone === 'gave_up';
    return (
      <div className={`crash-recovery-body${gaveUp ? ' crash-recovery-gaveup' : ''}`}>
        <div className="crash-recovery-headline">
          <span className="crash-recovery-icon" aria-hidden="true">{gaveUp ? '⛔' : '↻'}</span>
          <span>{crashView.text}</span>
        </div>
        <div className="crash-recovery-sub">
          {gaveUp
            ? 'De PC blijft bezet tot je dit voor deze bezoeker oplost.'
            : 'De bezoeker blijft op deze PC; de ervaring komt terug op dezelfde plek.'}
        </div>
        {run ? <div className="experience-name-v2">{run.name}</div> : null}
        {state.note ? <div className="crash-recovery-sub">{state.note}</div> : null}
        {renderQueueVisitor({ actions: gaveUp })}
      </div>
    );
  };

  // Constant per bundle, so hook order never changes across renders.
  if (!DEV_TOOLS_ENABLED) return null;

  const tileClass =
    `pc-tile online fake-tile` +
    (phase === 'gaveup' ? ' fake-tile-crashed' : '') +
    (phase === 'done' ? ' fake-tile-done' : '');

  return (
    <div className={tileClass}>
      {/* Header: same frame as PCTile, SIMULATIE instead of the BRIL/HP badges */}
      <div className="pc-tile-header-v2">
        <span className="player-index">{headerName}</span>
        <div className="device-batteries">
          <span
            className="battery-indicator fake-tile-badge"
            title="Gesimuleerde PC (staging): geen agent, geen headset. Alle releases van de ruimte gelden als geinstalleerd."
          >
            SIMULATIE
          </span>
        </div>
      </div>

      {nearlyDone && (
        <div className="notifications-container-v2">
          <div className="notification-v2 notification-warning">
            <span className="notification-icon-v2">⚠</span>
            <span className="notification-text-v2">Bezoeker is bijna klaar</span>
          </div>
        </div>
      )}

      {/* Gave up with no queue visitor (queue off / walk-in): the tile stays a
          normal free PC and a fresh launch clears the crash, like a real one. */}
      {crashView && !dispatchRecovering && (
        <div className="notifications-container-v2">
          <div className="notification-v2 notification-error">
            <span className="notification-icon-v2">⛔</span>
            <span className="notification-text-v2">{crashView.text}</span>
          </div>
        </div>
      )}

      <div className="pc-tile-body">
        {phase === 'running' && run ? (
          <div className="experience-running-v2">
            <div className="experience-running-info">
              <div className="experience-running-left">
                <div className="experience-name-v2">{run.name}</div>
                {run.language && (
                  <div className="experience-scene-v2">Taal: {run.language.toUpperCase()}</div>
                )}
              </div>
              <div className="experience-running-right">
                <div className="time-display">
                  <span className="time-elapsed">{formatClock(elapsedS)}</span>
                  <span className="time-separator"> / </span>
                  <span className="time-remaining">{formatClock(remainingS)}</span>
                </div>
              </div>
            </div>
            <div className="mini-progress-bar">
              <div className="mini-progress-fill" style={{ width: `${runPct}%` }} />
            </div>
            <div className="experience-running-actions">
              <button className="stop-btn-v2" onClick={handleStop} disabled={busy}>
                STOP
              </button>
            </div>
            {renderQueueVisitor()}
          </div>
        ) : phase === 'loading' && run ? (
          <div className="experience-loading-v2">
            <div className="experience-name-v2">{run.name}</div>
            <div className="fake-tile-loading">
              <span className="fake-tile-spinner" aria-hidden="true" />
              <span>Laden...{run.stuck ? ' (vastloper)' : ''}</span>
            </div>
            <div className="launch-progress-container-v2">
              <div className="launch-progress-bar-v2">
                <div className="launch-progress-fill-v2" style={{ width: `${loadPct}%` }} />
              </div>
              <span className="launch-progress-percent-v2">{Math.round(loadPct)}%</span>
            </div>
            <button className="stop-btn-v2" onClick={handleStop} disabled={busy}>
              STOP
            </button>
            {renderQueueVisitor()}
          </div>
        ) : phase === 'done' ? (
          <div className="fake-tile-flash fake-tile-flash-done">
            {run && <div className="experience-name-v2">{run.name}</div>}
            <div className="fake-tile-flash-text">Klaar</div>
          </div>
        ) : dispatchRecovering ? (
          /* Crashed: relaunch in flight, or the ladder gave up on this visitor */
          renderCrashRecoveryBody()
        ) : (
          <div className="experience-selection-v2">
            {experiences.length > 0 ? (
              <div className={`experience-launch-grid-v2${experiences.length > 2 ? ' compact' : ''}`}>
                {experiences.map((release) => {
                  const langs = release.experiences?.supported_languages ?? [];
                  const languageFlag = langs.length === 1 ? langs[0].language?.short_name : null;
                  const expColor = nameToColor(release.experiences?.name || 'Unknown');
                  const colorStyle = {
                    '--btn-color': expColor.border,
                    '--btn-bg': expColor.bg,
                  } as React.CSSProperties;
                  return (
                    <button
                      key={release.id}
                      className="launch-btn-v2"
                      style={colorStyle}
                      onClick={() => handleExperienceClick(release)}
                      disabled={busy}
                    >
                      <span className="launch-btn-name-v2">
                        {release.experiences?.name || 'Unknown'}
                        {isAdmin && release.experience_release?.version && (
                          <span className="version-tag">v{release.experience_release.version}</span>
                        )}
                        {languageFlag && (
                          <span className="language-flag-v2">{languageFlag.toUpperCase()}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="no-experiences-v2">No experiences available</p>
            )}
          </div>
        )}
      </div>

      {/* Language picker (2+ languages), same markup as PCTile */}
      {languagePick && (
        <div className="language-selection-modal">
          <div className="language-selection-content">
            <h3>Select Language</h3>
            <p className="experience-name">{languagePick.experiences?.name}</p>
            <div className="language-buttons">
              {(languagePick.experiences?.supported_languages ?? []).map((ls) => (
                <button
                  key={ls.id}
                  className="language-btn"
                  onClick={() =>
                    void handleLaunchExperience(
                      languagePick.experience_id,
                      languagePick.experience_release?.version || '1.0.0',
                      ls.language?.short_name || null,
                    )
                  }
                  disabled={busy}
                >
                  {hasLanguageFlag(ls.language?.short_name) ? (
                    <LanguageFlag code={ls.language?.short_name} className="language-flag-img-large" />
                  ) : (
                    <span className="language-flag-large">{(ls.language?.short_name || '').toUpperCase()}</span>
                  )}
                  <span className="language-name">{ls.language?.name}</span>
                </button>
              ))}
            </div>
            <button className="cancel-lang-btn" onClick={() => setLanguagePick(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Drill controls instead of the volume footer */}
      <div className="pc-tile-footer fake-tile-drill">
        <label
          className="fake-tile-drill-toggle"
          title="De volgende start blijft in Laden... hangen tot je stopt (geen injectie). Eenmalig."
        >
          <input
            type="checkbox"
            checked={state.stuckArmed}
            onChange={(e) => fakeAgentArmStuck(pcInstance.id, e.target.checked)}
          />
          Vastloper
        </label>
        <button
          className="fake-tile-drill-btn"
          onClick={handleCrash}
          disabled={phase !== 'loading' && phase !== 'running'}
          title="Laat de lopende ervaring crashen (exit 3). Eerste keer: automatisch herstarten met dezelfde bezoeker en dezelfde resterende tijd. Nog een crash binnen 2 minuten: auto-herstel stopt en je lost het met de herstelknoppen op."
        >
          Crash
        </button>
      </div>

      {error && <div className="error-message-v2">{error}</div>}
    </div>
  );
}
